import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/auth";
import { useQuery } from "@tanstack/react-query";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { Divider } from "primereact/divider";
import { Role } from "../../services/auth/auth.dto";
import dynamicSurveyService, { DynamicSurveyDto } from "../../services/forms/dynamic.service";

const quickAccessCards = [
  {
    title: "Hesaplama",
    description: "Yeşil dönüşüm ve sürdürülebilirlik hesaplamaları",
    path: "/app/karbonayakizi",
    icon: "pi pi-calculator",
    bg: "#EFF6FF",
    iconColor: "#1D4ED8",
  },
  {
    title: "Raporlama",
    description: "Sürdürülebilirlik ve çevresel performans raporları",
    path: "/app/rapor",
    icon: "pi pi-file-pdf",
    bg: "#F0FDF4",
    iconColor: "#15803D",
  },
  {
    title: "Analiz",
    description: "Yeşil dönüşüm olgunluk ve dijital dönüşüm analizi",
    path: "/app/grafik",
    icon: "pi pi-chart-line",
    bg: "#FAF5FF",
    iconColor: "#7E22CE",
  },
  {
    title: "Eğitim",
    description: "Sürdürülebilirlik ve teknoloji eğitimleri",
    path: "/app/egitim",
    icon: "pi pi-book",
    bg: "#FFF7ED",
    iconColor: "#C2410C",
  },
  {
    title: "İş Sürekliliği",
    description: "AFAD iş birliği ve iş sürekliliği planlaması",
    path: "/app/is-surekliligi",
    icon: "pi pi-shield",
    bg: "#FEF2F2",
    iconColor: "#B91C1C",
  },
  {
    title: "OSBÜK Bülten",
    description: "Güncel haberler ve sektörel gelişmeler",
    path: "/app/bulten",
    icon: "pi pi-envelope",
    bg: "#ECFEFF",
    iconColor: "#0E7490",
  },
];

function Homepage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [profileDialogVisible, setProfileDialogVisible] = useState(false);

  const user = auth.getUser();
  const displayName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || "Kullanıcı";

  useEffect(() => {
    const alreadyShown = sessionStorage.getItem("profileDialogShown");
    const missingName = !user.firstName || !user.lastName;
    if (missingName && !alreadyShown) {
      setProfileDialogVisible(true);
      sessionStorage.setItem("profileDialogShown", "1");
    }
  }, []);

  const { data: surveysData, isLoading: surveysLoading } = useQuery({
    queryKey: ["homepageSurveys"],
    queryFn: () => dynamicSurveyService.getSurveys(),
    enabled: !!user.access_token,
  });

  const allSurveys: DynamicSurveyDto[] = surveysData?.result?.data ?? [];
  const pendingSurveys = allSurveys.filter((s) => !s.has_submitted);
  const completedSurveys = allSurveys.filter((s) => s.has_submitted);

  return (
    <>
      <Dialog
        header="Profil Bilgileri Eksik"
        visible={profileDialogVisible}
        breakpoints={{ "640px": "92vw" }}
        style={{ width: "440px", maxWidth: "92vw" }}
        onHide={() => setProfileDialogVisible(false)}
      >
        <p className="m-0 text-600 line-height-3">
          Hesabınızda bazı bilgiler eksik. Lütfen profil sayfanızdan ad ve soyad bilgilerinizi tamamlayın.
        </p>
        <Button
          label="Profil Sayfasına Git"
          icon="pi pi-user-edit"
          className="mt-3 w-full"
          onClick={() => { setProfileDialogVisible(false); navigate("/app/profile"); }}
        />
      </Dialog>

      <div className="surface-0">

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div
          className="px-3 md:px-6 py-4 md:py-5"
          style={{ background: "linear-gradient(135deg, #0f4c6a 0%, #1a6e94 60%, #1e88b5 100%)" }}
        >
          <div className="flex align-items-start justify-content-between flex-wrap gap-3">
            <div>
              <p className="text-blue-200 m-0 text-sm font-medium mb-1 uppercase" style={{ letterSpacing: "0.08em" }}>
                Hoş Geldiniz
              </p>
              <h1 className="text-white font-bold m-0" style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)" }}>
                {displayName}
              </h1>
              <p className="text-blue-100 mt-2 mb-0 text-base" style={{ opacity: 0.85 }}>
                OSBÜK Sürdürülebilirlik ve Dönüşüm Platformu
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              {pendingSurveys.length > 0 && (
                <div
                  className="flex align-items-center gap-2 px-3 py-2 border-round-xl"
                  style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)" }}
                >
                  <i className="pi pi-bell text-yellow-300" style={{ fontSize: "1.1rem" }} />
                  <span className="text-white font-semibold text-sm">
                    {pendingSurveys.length} bekleyen anket
                  </span>
                </div>
              )}
              {completedSurveys.length > 0 && (
                <div
                  className="flex align-items-center gap-2 px-3 py-2 border-round-xl"
                  style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)" }}
                >
                  <i className="pi pi-check-circle text-green-300" style={{ fontSize: "1.1rem" }} />
                  <span className="text-white font-semibold text-sm">
                    {completedSurveys.length} tamamlanan anket
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-3 md:px-6 py-4 md:py-5">

          {/* ── Aktif Anketler ──────────────────────────────────────────── */}
          {user.access_token && (
            <section className="mb-6">
              <div className="flex align-items-center justify-content-between mb-3">
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-list-check text-primary" style={{ fontSize: "1.2rem" }} />
                  <h2 className="text-900 font-bold text-xl m-0">Aktif Anketler</h2>
                </div>
                <Button
                  label="Tümünü Gör"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  text
                  size="small"
                  onClick={() => navigate("/app/anketler")}
                />
              </div>

              {surveysLoading ? (
                <div className="flex justify-content-center py-4">
                  <ProgressSpinner style={{ width: "36px", height: "36px" }} />
                </div>
              ) : allSurveys.length === 0 ? (
                <div
                  className="flex align-items-center gap-3 p-4 border-round-xl"
                  style={{ background: "var(--surface-50)", border: "1px solid var(--surface-200)" }}
                >
                  <i className="pi pi-info-circle text-400" style={{ fontSize: "1.4rem" }} />
                  <span className="text-500">Şu anda aktif anket bulunmuyor.</span>
                </div>
              ) : (
                <div className="grid">
                  {allSurveys.slice(0, 4).map((survey) => (
                    <div key={survey.id} className="col-12 md:col-6 lg:col-3">
                      <div
                        className="flex flex-column border-round-xl p-4 h-full"
                        style={{
                          background: survey.has_submitted ? "var(--surface-50)" : "var(--primary-50, #EFF6FF)",
                          border: `2px solid ${survey.has_submitted ? "var(--surface-200)" : "var(--primary-200, #BFDBFE)"}`,
                          transition: "box-shadow 0.2s",
                        }}
                      >
                        <div className="flex align-items-start justify-content-between mb-2">
                          <div
                            className="flex align-items-center justify-content-center border-round-lg"
                            style={{
                              width: "36px",
                              height: "36px",
                              background: survey.has_submitted ? "var(--surface-200)" : "var(--primary-100, #DBEAFE)",
                            }}
                          >
                            <i
                              className={survey.has_submitted ? "pi pi-check text-500" : "pi pi-clipboard text-primary"}
                              style={{ fontSize: "1rem" }}
                            />
                          </div>
                          <Tag
                            value={survey.has_submitted ? "Tamamlandı" : "Bekliyor"}
                            severity={survey.has_submitted ? "success" : "warning"}
                            style={{ fontSize: "0.68rem" }}
                          />
                        </div>
                        <h4 className="text-900 font-semibold m-0 mb-1" style={{ fontSize: "0.9rem", lineHeight: "1.4" }}>
                          {survey.title}
                        </h4>
                        {survey.description && (
                          <p className="text-500 text-xs m-0 mb-3 flex-1" style={{ lineHeight: "1.5" }}>
                            {survey.description.length > 60
                              ? survey.description.slice(0, 60) + "…"
                              : survey.description}
                          </p>
                        )}
                        <Button
                          label={survey.has_submitted ? "Tamamlandı" : "Ankete Katıl"}
                          icon={survey.has_submitted ? "pi pi-check" : "pi pi-arrow-right"}
                          iconPos="right"
                          size="small"
                          disabled={survey.has_submitted}
                          severity={survey.has_submitted ? "secondary" : undefined}
                          outlined={survey.has_submitted}
                          className="w-full mt-auto"
                          onClick={() => navigate(`/app/anketler/${survey.share_token || survey.id}`)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          <Divider />

          {/* ── Hızlı Erişim ───────────────────────────────────────────── */}
          <section className="mb-6">
            <div className="flex align-items-center gap-2 mb-3">
              <i className="pi pi-th-large text-primary" style={{ fontSize: "1.2rem" }} />
              <h2 className="text-900 font-bold text-xl m-0">Hızlı Erişim</h2>
            </div>
            <div className="grid">
              {quickAccessCards.map((card, i) => (
                <div key={i} className="col-12 md:col-6 lg:col-4">
                  <div
                    className="flex align-items-center gap-3 p-3 border-round-xl cursor-pointer"
                    style={{
                      border: "1px solid var(--surface-200)",
                      transition: "box-shadow 0.2s, transform 0.15s",
                      background: "#fff",
                    }}
                    onClick={() => navigate(card.path)}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)";
                      (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
                      (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
                    }}
                  >
                    <div
                      className="flex align-items-center justify-content-center border-round-lg flex-shrink-0"
                      style={{ width: "44px", height: "44px", background: card.bg }}
                    >
                      <i className={card.icon} style={{ fontSize: "1.2rem", color: card.iconColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-900 font-semibold m-0 text-sm">{card.title}</p>
                      <p className="text-500 text-xs m-0 mt-1" style={{ lineHeight: "1.4" }}>
                        {card.description}
                      </p>
                    </div>
                    <i className="pi pi-chevron-right text-300 text-xs flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── Topluluk (sadece OSBÜK kullanıcılarına) ─────────────────── */}
          {(user.role === Role.OSBUK_USER || user.role === Role.ADMIN) && (
            <>
              <Divider />
              <section>
                <div className="flex align-items-center gap-2 mb-3">
                  <i className="pi pi-users text-primary" style={{ fontSize: "1.2rem" }} />
                  <h2 className="text-900 font-bold text-xl m-0">Topluluk</h2>
                </div>
                <div className="grid">
                  {[
                    {
                      title: "Forum",
                      desc: "OSB'ler arası iletişim ve bilgi paylaşımı",
                      icon: "pi pi-comments",
                      bg: "#ECFEFF",
                      color: "#0E7490",
                      path: "/app/forum-homepage",
                    },
                    {
                      title: "Stratejik Plan Çalışması",
                      desc: "Stratejik planlama ve uygulama yöntemleri",
                      icon: "pi pi-compass",
                      bg: "#EFF6FF",
                      color: "#1D4ED8",
                      path: "/app/stratejik-plan",
                    },
                    {
                      title: "Yapay Zeka Sohbeti",
                      desc: "OSBÜK hakkında AI destekli soru-cevap",
                      icon: "pi pi-comment",
                      bg: "#FAF5FF",
                      color: "#7E22CE",
                      path: "/app/yapay-zeka",
                    },
                  ].map((item, i) => (
                    <div key={i} className="col-12 md:col-4">
                      <Card
                        className="cursor-pointer h-full border-round-xl shadow-none"
                        style={{ border: "1px solid var(--surface-200)" }}
                        onClick={() => navigate(item.path)}
                      >
                        <div className="flex align-items-center gap-3">
                          <div
                            className="flex align-items-center justify-content-center border-round-lg flex-shrink-0"
                            style={{ width: "44px", height: "44px", background: item.bg }}
                          >
                            <i className={item.icon} style={{ fontSize: "1.2rem", color: item.color }} />
                          </div>
                          <div>
                            <p className="text-900 font-semibold m-0 text-sm">{item.title}</p>
                            <p className="text-500 text-xs m-0 mt-1">{item.desc}</p>
                          </div>
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

        </div>
      </div>
    </>
  );
}

export default Homepage;
